import React, { useState } from 'react';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, CheckIcon } from 'lucide-react';
import { BackLink } from '../components/BackLink';
type SchedulePathType = 'describer' | 'planner' | null;
export const ManageSchedulePage = () => {
  const [selectedPath, setSelectedPath] = useState<SchedulePathType>(null);
  const [scheduleDescription, setScheduleDescription] = useState('');
  const [aiInterpretation, setAiInterpretation] = useState<string | null>(null);
  const [selectedCalendar, setSelectedCalendar] = useState<string | null>(null);
  const [schedulingHorizon, setSchedulingHorizon] = useState('2 weeks');
  const [travelBuffer, setTravelBuffer] = useState('30 mins');
  const [flexibility, setFlexibility] = useState(50);
  const handlePathSelect = (path: SchedulePathType) => {
    setSelectedPath(path);
    setAiInterpretation(null);
  };
  const handleDescriptionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate AI interpretation
    setAiInterpretation(`
      Okay, got it! So, it looks like:
      Weeknights: You're generally available after 6 PM.
      Weekends: You're open for anything.
      Fridays/Saturdays: Prime time for going out.
      Does that sound right?
    `);
  };
  const handleCalendarConnect = (provider: string) => {
    // Simulate OAuth flow
    setSelectedCalendar(provider);
  };
  const exampleSchedules = ["I work a standard 9-to-5, but I'm usually free after 6 PM on weekdays. Weekends are completely open...", "I'm a student with classes Mon/Weds/Fri from 10am to 3pm. I like to go out on Friday and Saturday nights...", "My schedule is unpredictable, but I'm usually free on weekends. I'll watch games during the week at home, but only go out for them on Saturday."];
  return <div className="w-full min-h-screen bg-[#1A1A1D] text-[#F5F5F7]" data-id="element-948">
      <div className="max-w-4xl mx-auto p-6" data-id="element-949">
        <div className="mb-8" data-id="element-950">
          <BackLink to="/app" className="mb-4" data-id="element-951">
            Back to Dashboard
          </BackLink>
          <h1 className="text-3xl font-bold mb-2" data-id="element-952">Manage My Schedule</h1>
          <p className="text-gray-400" data-id="element-953">
            Help us understand when you're available so we can recommend events
            that fit your life.
          </p>
        </div>
        {!selectedPath ? <div className="bg-[#22252A] rounded-xl p-6 shadow-lg" data-id="element-954">
            <h2 className="text-xl font-bold mb-6 text-center" data-id="element-955">
              How do you manage your time?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-956">
              <button onClick={() => handlePathSelect('describer')} className="bg-[#2C2F33] hover:bg-[#3A3D42] p-6 rounded-xl transition-colors border border-[#3A3D42] text-left" data-id="element-957">
                <div className="w-12 h-12 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white mb-4" data-id="element-958">
                  <ClockIcon size={24} data-id="element-959" />
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-960">
                  Describe Your Availability
                </h3>
                <p className="text-gray-400" data-id="element-961">
                  Tell us about your typical week in your own words. We'll
                  figure out when you're free.
                </p>
              </button>
              <button onClick={() => handlePathSelect('planner')} className="bg-[#2C2F33] hover:bg-[#3A3D42] p-6 rounded-xl transition-colors border border-[#3A3D42] text-left" data-id="element-962">
                <div className="w-12 h-12 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white mb-4" data-id="element-963">
                  <CalendarIcon size={24} data-id="element-964" />
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-965">
                  Connect Your Calendar
                </h3>
                <p className="text-gray-400" data-id="element-966">
                  Securely connect your digital calendar so we can see when
                  you're busy (but not what you're doing).
                </p>
              </button>
            </div>
          </div> : selectedPath === 'describer' ? <div className="bg-[#22252A] rounded-xl p-6 shadow-lg" data-id="element-967">
            <div className="flex items-center mb-6" data-id="element-968">
              <button onClick={() => handlePathSelect(null)} className="mr-4 p-2 hover:bg-[#3A3D42] rounded-full" data-id="element-969">
                <ArrowLeftIcon size={16} data-id="element-970" />
              </button>
              <h2 className="text-xl font-bold" data-id="element-971">Describe Your Availability</h2>
            </div>
            {!aiInterpretation ? <form onSubmit={handleDescriptionSubmit} data-id="element-972">
                <div className="mb-6" data-id="element-973">
                  <label className="block mb-2 font-medium" data-id="element-974">
                    In your own words, tell us about your typical week. The more
                    detail, the better!
                  </label>
                  <textarea value={scheduleDescription} onChange={e => setScheduleDescription(e.target.value)} placeholder={exampleSchedules[0]} className="w-full h-40 p-4 bg-[#2C2F33] border border-[#3A3D42] rounded-lg text-[#F5F5F7] focus:outline-none focus:border-[#007BFF]" required data-id="element-975" />
                  <div className="mt-2 text-sm text-gray-400" data-id="element-976">
                    <p data-id="element-977">Examples:</p>
                    <ul className="mt-1 space-y-1" data-id="element-978">
                      {exampleSchedules.map((example, index) => <li key={index} className="cursor-pointer hover:text-gray-300" onClick={() => setScheduleDescription(example)} data-id="element-979">
                          "{example.substring(0, 60)}..."
                        </li>)}
                    </ul>
                  </div>
                </div>
                <button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors" data-id="element-980">
                  Submit
                </button>
              </form> : <div className="bg-[#2C2F33] p-6 rounded-lg border border-[#3A3D42]" data-id="element-981">
                <h3 className="font-bold text-lg mb-4" data-id="element-982">Our Understanding</h3>
                <div className="whitespace-pre-line text-gray-300 mb-6" data-id="element-983">
                  {aiInterpretation}
                </div>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-984">
                  <button onClick={() => setAiInterpretation(null)} className="flex-1 border border-[#3A3D42] px-6 py-3 rounded-lg hover:bg-[#3A3D42] transition-colors" data-id="element-985">
                    Edit Description
                  </button>
                  <button onClick={() => {
              // Save the interpretation and go back to main screen
              alert('Schedule preferences saved!');
              handlePathSelect(null);
            }} className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors flex items-center justify-center" data-id="element-986">
                    <CheckIcon size={18} className="mr-2" data-id="element-987" />
                    Confirm
                  </button>
                </div>
              </div>}
          </div> : <div className="bg-[#22252A] rounded-xl p-6 shadow-lg" data-id="element-988">
            <div className="flex items-center mb-6" data-id="element-989">
              <button onClick={() => handlePathSelect(null)} className="mr-4 p-2 hover:bg-[#3A3D42] rounded-full" data-id="element-990">
                <ArrowLeftIcon size={16} data-id="element-991" />
              </button>
              <h2 className="text-xl font-bold" data-id="element-992">Connect Your Calendar</h2>
            </div>
            {!selectedCalendar ? <div data-id="element-993">
                <p className="mb-6 text-gray-400" data-id="element-994">
                  We'll only look at whether you're busy or free, never what the
                  events are.
                </p>
                <div className="space-y-4 mb-6" data-id="element-995">
                  <button onClick={() => handleCalendarConnect('Google')} className="w-full bg-[#2C2F33] hover:bg-[#3A3D42] border border-[#3A3D42] p-4 rounded-lg flex items-center" data-id="element-996">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-997">
                      <span className="text-red-500 font-bold" data-id="element-998">G</span>
                    </div>
                    Connect with Google Calendar
                  </button>
                  <button onClick={() => handleCalendarConnect('Outlook')} className="w-full bg-[#2C2F33] hover:bg-[#3A3D42] border border-[#3A3D42] p-4 rounded-lg flex items-center" data-id="element-999">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4" data-id="element-1000">
                      <span className="text-white font-bold" data-id="element-1001">O</span>
                    </div>
                    Connect with Microsoft Outlook
                  </button>
                  <button onClick={() => handleCalendarConnect('Apple')} className="w-full bg-[#2C2F33] hover:bg-[#3A3D42] border border-[#3A3D42] p-4 rounded-lg flex items-center" data-id="element-1002">
                    <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center mr-4" data-id="element-1003">
                      <span className="text-white font-bold" data-id="element-1004">A</span>
                    </div>
                    Connect with Apple Calendar
                  </button>
                </div>
              </div> : <div data-id="element-1005">
                <div className="bg-[#2C2F33] p-4 rounded-lg border border-[#3A3D42] mb-6 flex items-center" data-id="element-1006">
                  <CheckIcon className="text-green-500 mr-2" size={20} data-id="element-1007" />
                  <span data-id="element-1008">
                    Successfully connected to {selectedCalendar} Calendar
                  </span>
                </div>
                <div className="space-y-6" data-id="element-1009">
                  <div data-id="element-1010">
                    <label className="block mb-2 font-medium" data-id="element-1011">
                      How far in advance is your calendar usually accurate?
                    </label>
                    <select value={schedulingHorizon} onChange={e => setSchedulingHorizon(e.target.value)} className="w-full p-3 bg-[#2C2F33] border border-[#3A3D42] rounded-lg text-[#F5F5F7] focus:outline-none focus:border-[#007BFF]" data-id="element-1012">
                      <option value="1 week" data-id="element-1013">1 week</option>
                      <option value="2 weeks" data-id="element-1014">2 weeks</option>
                      <option value="1 month" data-id="element-1015">1 month</option>
                      <option value="3 months" data-id="element-1016">3 months</option>
                    </select>
                  </div>
                  <div data-id="element-1017">
                    <label className="block mb-2 font-medium" data-id="element-1018">
                      How much travel time should we typically add before and
                      after an event?
                    </label>
                    <select value={travelBuffer} onChange={e => setTravelBuffer(e.target.value)} className="w-full p-3 bg-[#2C2F33] border border-[#3A3D42] rounded-lg text-[#F5F5F7] focus:outline-none focus:border-[#007BFF]" data-id="element-1019">
                      <option value="15 mins" data-id="element-1020">15 minutes</option>
                      <option value="30 mins" data-id="element-1021">30 minutes</option>
                      <option value="1 hour" data-id="element-1022">1 hour</option>
                      <option value="1.5 hours" data-id="element-1023">1.5 hours</option>
                    </select>
                  </div>
                  <div data-id="element-1024">
                    <label className="block mb-2 font-medium" data-id="element-1025">
                      How willing are you to reschedule for an unmissable event?
                    </label>
                    <div className="flex items-center" data-id="element-1026">
                      <span className="mr-4 text-sm" data-id="element-1027">Not flexible</span>
                      <input type="range" min="0" max="100" value={flexibility} onChange={e => setFlexibility(parseInt(e.target.value))} className="flex-1" data-id="element-1028" />
                      <span className="ml-4 text-sm" data-id="element-1029">Very flexible</span>
                    </div>
                  </div>
                  <button onClick={() => {
              // Save the calendar preferences
              alert('Calendar preferences saved!');
              handlePathSelect(null);
            }} className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors mt-4" data-id="element-1030">
                    Save Preferences
                  </button>
                </div>
              </div>}
          </div>}
        <div className="mt-6 bg-[#2C2F33] p-4 rounded-lg border border-[#3A3D42]" data-id="element-1031">
          <div className="flex items-center" data-id="element-1032">
            <div className="w-10 h-10 bg-[#007BFF] bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-1033">
              <CheckIcon size={20} className="text-[#007BFF]" data-id="element-1034" />
            </div>
            <div data-id="element-1035">
              <h3 className="font-medium" data-id="element-1036">Privacy Guarantee</h3>
              <p className="text-sm text-gray-400" data-id="element-1037">
                Your schedule information is private and used solely to improve
                your recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};